import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from "react-router-dom";
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';
import moment from 'moment';

import Paginator from '../../components/ui/Paginator';
import { StockMovement } from '../../models/interfaces';
import { StocksContext } from '../../context/stock/StockContext';
import { accountingBadge } from './inventoryAccounting';
import { formatCost, formatMoney, kindLabel } from './costing';


const PAGE_SIZE = 15;

const today = () => moment().format('YYYY-MM-DD');

const movementBadge: Record<string, string> = {
    Entrada: 'bg-success-subtle text-success-emphasis',
    Salida: 'bg-danger-subtle text-danger-emphasis',
    Ajuste: 'bg-primary-subtle text-primary-emphasis',
};

export const StockMovementsPage = () => {
    const [buscar, setBuscar] = useState('');
    const [movementType, setMovementType] = useState<string>('all');
    // Por defecto se muestran los movimientos de hoy
    const [startDate, setStartDate] = useState<string>(today());
    const [endDate, setEndDate] = useState<string>(today());
    const [pageActual, setPageActual] = useState<number>(1);

    const [movements, setMovements] = useState<StockMovement[]>([]);
    const [movementTypes, setMovementTypes] = useState<string[]>([]);
    const [totalPage, setTotalPage] = useState<number>(0);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);

    const { loadMovements } = useContext(StocksContext);
    const navigate = useNavigate();

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pickerRef = useRef<DateRangePicker>(null);

    const fetchMovements = async (page: number) => {
        setIsLoading(true);
        const resp = await loadMovements(page - 1, PAGE_SIZE, { search: buscar, movementType, startDate, endDate });
        setIsLoading(false);
        if (resp) {
            setMovements(resp.movements ?? []);
            setMovementTypes(resp.movementTypes ?? []);
            setTotalPage(resp.totalPages ?? 1);
            setTotalRecords(resp.totalRecords ?? 0);
        } else {
            setMovements([]);
            setTotalPage(0);
            setTotalRecords(0);
        }
    }

    // Recarga cuando cambia la página, tipo o fechas
    useEffect(() => {
        fetchMovements(pageActual);
    }, [pageActual, movementType, startDate, endDate])

    // Búsqueda con debounce
    const firstRender = useRef(true);
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (pageActual !== 1) {
                setPageActual(1);
            } else {
                fetchMovements(1);
            }
        }, 400);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [buscar])

    const handleCallback = (start, end) => {
        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end.format('YYYY-MM-DD'));
        setPageActual(1);
    }

    const clearDates = () => {
        setStartDate('');
        setEndDate('');
        setPageActual(1);
    }

    const dateLabel = startDate && endDate
        ? `${moment(startDate).format('DD/MM/YYYY')} - ${moment(endDate).format('DD/MM/YYYY')}`
        : '';

    const clearFilters = () => {
        setBuscar('');
        setMovementType('all');
        setStartDate(today());
        setEndDate(today());
        pickerRef.current?.setStartDate(moment());
        pickerRef.current?.setEndDate(moment());
        setPageActual(1);
    }

    // Columna "Contabilidad" solo si algún movimiento tiene estado (integración de inventario activa)
    const showAccounting = movements.some(mv => !!mv.accountingState);

    const startIndex = totalRecords === 0 ? 0 : (pageActual - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Movimientos de inventario</h3>
                    <p className="text-muted mb-0">Historial completo de entradas, salidas y ajustes de stock.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/stocks')}>
                        <i className="bi bi-arrow-left me-1" /> Volver al inventario
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    {/* Filtros */}
                    <div className="row g-2 mb-3">
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input
                                    className="form-control"
                                    type="text"
                                    placeholder="Buscar por producto o código..."
                                    value={buscar}
                                    onChange={(e) => setBuscar(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={movementType}
                                onChange={(e) => { setMovementType(e.target.value); setPageActual(1); }}
                            >
                                <option value="all">Todos los tipos</option>
                                {movementTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <DateRangePicker
                                ref={pickerRef}
                                onCallback={handleCallback}
                                onCancel={clearDates}
                                initialSettings={{
                                    autoUpdateInput: false,
                                    locale: {
                                        cancelLabel: 'Limpiar',
                                        applyLabel: 'Aplicar',
                                        customRangeLabel: 'Seleccionar fechas'
                                    },
                                    startDate: moment(),
                                    endDate: moment(),
                                    maxDate: moment(),
                                    ranges: {
                                        'Hoy': [moment(), moment()],
                                        'Ayer': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                                        'Esta semana': [moment().startOf('week'), moment().endOf('week')],
                                        'Este mes': [moment().startOf('month'), moment()],
                                        'Últimos 7 días': [moment().subtract(6, 'days'), moment()],
                                        'Último 30 días': [moment().subtract(30, 'days'), moment()],
                                    }
                                }}
                            >
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Todas las fechas"
                                    value={dateLabel}
                                    readOnly
                                />
                            </DateRangePicker>
                        </div>
                        <div className="col-md-2 d-grid">
                            <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}>
                                <i className="bi bi-x-circle me-1" /> Limpiar
                            </button>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Fecha</th>
                                    <th>Código</th>
                                    <th>Producto</th>
                                    <th>Tipo</th>
                                    <th className="text-end">Anterior</th>
                                    <th className="text-end">Cambio</th>
                                    <th className="text-end">Nuevo</th>
                                    <th className="text-end">Costo unitario</th>
                                    <th className="text-end">Valor</th>
                                    {/* <th>Factura</th> */}
                                    <th>Usuario</th>
                                    <th>Nota</th>
                                    {showAccounting && <th>Contabilidad</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={12} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : movements.length > 0 ? (
                                    movements.map(mv => {
                                        const previous = Number(mv.previousStock);
                                        const current = Number(mv.newStock);
                                        const diff = current - previous;
                                        return (
                                            <tr key={mv.id}>
                                                <td className="text-nowrap small">{mv.updateDate ? new Date(mv.updateDate).toLocaleString() : '-'}</td>
                                                <td className="text-muted">{mv.productCode ?? '-'}</td>
                                                <td className="fw-medium">{mv.productName ?? `Producto #${mv.inventoryProductId}`}</td>
                                                <td>
                                                    <span className={`badge rounded-pill ${movementBadge[mv.movementType] ?? 'bg-secondary-subtle text-secondary-emphasis'}`}>
                                                        {kindLabel(mv.kind, mv.movementType)}
                                                    </span>
                                                </td>
                                                <td className="text-end">{previous}</td>
                                                <td className={`text-end fw-semibold ${diff >= 0 ? 'text-success' : 'text-danger'}`}>
                                                    {diff >= 0 ? '+' : '-'}{Math.abs(diff)}
                                                </td>
                                                <td className="text-end">{current} {mv.unit ?? ''}</td>
                                                <td className="text-end">{mv.unitCost === null || mv.unitCost === undefined ? '-' : formatCost(mv.unitCost)}</td>
                                                <td className={`text-end ${Number(mv.value) < 0 ? 'text-danger' : ''}`}>{mv.value === null || mv.value === undefined ? '-' : formatMoney(mv.value)}</td>
                                                {/* <td>{mv.invoiceNumber ?? '-'}</td> */}
                                                <td>{mv.userName ?? mv.user ?? '-'}</td>
                                                <td className="small text-muted">{mv.note ?? '-'}</td>
                                                {showAccounting && (
                                                    <td className="small" style={{ maxWidth: 260 }}>
                                                        {mv.accountingState ? (
                                                            <>
                                                                <span
                                                                    className={`badge rounded-pill ${accountingBadge[mv.accountingState]}`}
                                                                    title={[mv.accountingCode, mv.accountingEntryCode ? `Asiento ${mv.accountingEntryCode}` : null].filter(Boolean).join(' · ') || undefined}
                                                                >
                                                                    {mv.accountingState}
                                                                </span>
                                                                {mv.accountingMessage && (
                                                                    <div className={mv.accountingState === 'Error' ? 'text-danger mt-1' : 'text-muted mt-1'}>{mv.accountingMessage}</div>
                                                                )}
                                                            </>
                                                        ) : <span className="text-muted">-</span>}
                                                    </td>
                                                )}
                                            </tr>
                                        )
                                    })
                                ) : (
                                    <tr><td colSpan={13} className='text-center py-4 text-muted'>No hay movimientos para los filtros seleccionados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <span className="text-muted small">
                            {totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} movimientos` : ''}
                        </span>
                        {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
};
